import { Severity } from '@sentry/browser';
import { InteractionName } from '../types/InteractionName';
import { type WidgetFedopsLogger } from '../components/Provider/Fedops/FedopsProvider';
import { logGeneralError } from './sentry';

export const PAYPAL_SUPPORTED_CURRENCIES = [
  'AUD',
  'BRL',
  'CAD',
  'CZK',
  'EUR',
  'HKD',
  'HUF',
  'INR',
  'ILS',
  'JPY',
  'MYR',
  'MXN',
  'TWD',
  'NZD',
  'NOK',
  'PHP',
  'PLN',
  'GBP',
  'RUB',
  'SGD',
  'SEK',
  'CHF',
  'THB',
  'USD',
];
export const PAYPAL_SCRIPT_URL = 'https://www.paypal.com/sdk/js?';

export interface Options {
  testBuyerCountry: boolean;
  bnCode: string;
  merchantId: string;
  clientId: string;
  currency: string;
  fedopsLogger: WidgetFedopsLogger;
}

export const buildQueryAndLoadPayPalScript = (options: Options, appendScript = addScript): Promise<void> => {
  const q = {
    'client-id': options.clientId,
    commit: false,
    currency: options.currency,
    components: 'buttons,funding-eligibility',
    'enable-funding': 'paylater,venmo',
  };

  // should be filled for production and empty in dev mode
  if (options.merchantId && options.merchantId !== 'unknown') {
    q['merchant-id'] = options.merchantId;
  }
  if (options.testBuyerCountry) {
    q['buyer-country'] = 'US';
  }
  const query = Object.keys(q).reduce((prev, key) => {
    prev += `&${encodeURIComponent(key)}=${encodeURIComponent(q[key])}`;
    return prev;
  }, '');
  const config = { src: PAYPAL_SCRIPT_URL + query, 'data-partner-attribution-id': options.bnCode };

  const cachedPpScripts = getGlobalPayPalScripts();
  let scriptPromise = cachedPpScripts.scriptPromise;

  if (cachedPpScripts.query !== query) {
    options.fedopsLogger.interactionStarted(InteractionName.PayPalLoadScript);

    if (findPayPalScript()) {
      removePayPalScript();
    }
    cachedPpScripts.query = query;
    scriptPromise = cachedPpScripts.scriptPromise = appendScript(config).then(() => {
      options.fedopsLogger.interactionEnded(InteractionName.PayPalLoadScript);
    });
    scriptPromise.catch((e) => {
      const currencyNotSupported: boolean = PAYPAL_SUPPORTED_CURRENCIES.indexOf(options.currency) === -1;
      if (currencyNotSupported) {
        return logGeneralError({
          message: 'PayPal: Currency is not supported',
          severity: Severity.Info,
          fingerprint: ['CurrencyNotSupportedError'],
          payload: {
            pp_src: e.target.src,
          },
        });
      }
      logGeneralError({
        message: 'XHR: Load PayPal script failed.',
        payload: {
          pp_src: e?.target?.src,
          message: e.message,
        },
        fingerprint: ['ScriptLoadError'],
      });
    });
  }
  return scriptPromise;
};

export const addScript = (config: {
  src: string;
  'data-partner-attribution-id': string;
}): Promise<HTMLScriptElement> => {
  return new Promise<HTMLScriptElement>((resolve, reject) => {
    const scriptElem = document.createElement('script');
    if (typeof config === 'object') {
      // copy every property to the element
      for (const key in config) {
        if (Object.prototype.hasOwnProperty.call(config, key)) {
          scriptElem.setAttribute(key, config[key]);
        }
      }
    }
    scriptElem.addEventListener('load', () => resolve(scriptElem));
    scriptElem.addEventListener('error', (e) => reject(e));
    document.body.appendChild(scriptElem);
    return scriptElem;
  });
};

const findPayPalScript = () => document.querySelector<HTMLScriptElement>(`script[src^="${PAYPAL_SCRIPT_URL}"]`);
const removePayPalScript = () => findPayPalScript().remove();

const getGlobalPayPalScripts = (): {
  query: string;
  scriptPromise: Promise<void>;
} => {
  window.__WIX_PAYMENTS__ = window.__WIX_PAYMENTS__ || {};
  return (window.__WIX_PAYMENTS__.__cachedPayPalScript = window.__WIX_PAYMENTS__.__cachedPayPalScript || {});
};
